import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Briefcase, Plus } from "lucide-react";
import { motion } from "framer-motion";
import { supabase } from "@/lib/supabase";
import styles from "./onboarding.module.css";

interface ProfessionalInfoStepProps {
	onNext: (data: any) => void;
	onBack: () => void;
	initialData?: any;
}

export const ProfessionalInfoStep = ({
	onNext,
	onBack,
	initialData,
}: ProfessionalInfoStepProps) => {
	const [bio, setBio] = useState(initialData?.bio || "");
	const [servicesOffered, setServicesOffered] = useState<string[]>(
		initialData?.servicesOffered || [],
	);
	const [newService, setNewService] = useState("");
	const [loading, setLoading] = useState(false);

	const handleAddService = () => {
		if (!newService.trim()) return;

		if (servicesOffered.includes(newService.trim())) {
			alert("Service already exists");
			return;
		}

		setServicesOffered((prev) => [...prev, newService.trim()]);
		setNewService("");
	};

	const handleRemoveService = (serviceToRemove: string) => {
		setServicesOffered((prev) =>
			prev.filter((service) => service !== serviceToRemove),
		);
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!bio.trim()) {
			alert("Please add a professional bio");
			return;
		}

		if (servicesOffered.length === 0) {
			alert("Please add at least one service");
			return;
		}

		setLoading(true);

		try {
			// Call the edge function to save progress
			const response = await fetch(
				`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/update-doula-profile`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
					},
					body: JSON.stringify({
						step: "professional_info",
						data: {
							professional_info: {
								bio: bio.trim(),
								services_offered: servicesOffered,
							},
						},
					}),
				},
			);

			const result = await response.json();

			if (!response.ok) {
				throw new Error(
					result.error || "Failed to save professional information",
				);
			}

			onNext({ bio: bio.trim(), servicesOffered });
		} catch (error) {
			console.error("Error saving professional information:", error);
			alert("Failed to save professional information. Please try again.");
		} finally {
			setLoading(false);
		}
	};

	return (
		<motion.div
			initial={{ opacity: 0, y: 20 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.6 }}
			className={styles.wrapper}
		>
			{/* Progress Bar */}
			<div className={styles.progressSection}>
				<div className={styles.progressHeader}>
					<span className={styles.progressLabel}>Step 3 of 15</span>
					<span className={styles.progressLabel}>Professional Information</span>
				</div>
				<div className={styles.progressTrack}>
					<div className={styles.progressFill} style={{ width: "27%" }} />
				</div>
			</div>

			{/* Main Card */}
			<Card className={styles.card}>
				<CardHeader className="text-center pb-4">
					<CardTitle className={styles.cardTitle}>
						<Briefcase className={styles.cardIcon} />
						Professional Information
					</CardTitle>
					<p className={styles.cardSubtitle}>
						Tell families about your approach and the services you offer.
					</p>
				</CardHeader>

				<CardContent className="space-y-6">
					<form onSubmit={handleSubmit} className="space-y-6">
						{/* Professional Bio */}
						<div>
							<Label htmlFor="bio" className={styles.fieldLabel}>
								Professional Bio *
							</Label>
							<Textarea
								id="bio"
								placeholder="Tell parents about your approach, philosophy, and what makes you unique as a doula..."
								value={bio}
								onChange={(e) => setBio(e.target.value)}
								rows={5}
								className="mt-2 resize-none"
							/>
							<p className={styles.helperText}>
								This will appear on your public profile and help families
								understand your approach.
							</p>
						</div>

						{/* Services Offered */}
						<div>
							<Label className={styles.fieldLabel}>Services Offered *</Label>
							<div className="mt-2 space-y-4">
								{/* Current Services */}
								<div className="flex flex-wrap gap-2">
									{servicesOffered.length > 0 ? (
										servicesOffered.map((service, index) => (
											<Badge
												key={index}
												variant="secondary"
												className={`${styles.badge} px-3 py-2 flex items-center gap-2`}
											>
												{service}
												<button
													onClick={() => handleRemoveService(service)}
													className="hover:text-red-600 transition-colors text-lg leading-none"
													type="button"
												>
													×
												</button>
											</Badge>
										))
									) : (
										<p className="text-sm font-karla italic opacity-50">
											No services added yet
										</p>
									)}
								</div>

								{/* Add New Service */}
								<div className="flex gap-2">
									<Input
										placeholder="e.g., Birth Support, Postpartum Care, Lactation Consulting..."
										value={newService}
										onChange={(e) => setNewService(e.target.value)}
										onKeyPress={(e) =>
											e.key === "Enter" &&
											(e.preventDefault(), handleAddService())
										}
										className="flex-1"
									/>
									<Button
										onClick={handleAddService}
										disabled={!newService.trim()}
										className={styles.submitButton}
										type="button"
									>
										<Plus className="h-4 w-4 mr-1" />
										Add
									</Button>
								</div>
								<p className={styles.helperText}>
									Add the services you offer to help families find the right
									support.
								</p>
							</div>
						</div>

						{/* Navigation Buttons */}
						<div className={styles.navRow}>
							<Button
								type="button"
								variant="outline"
								onClick={onBack}
								className={styles.backButton}
							>
								Back
							</Button>
							<Button
								type="submit"
								disabled={!bio.trim() || servicesOffered.length === 0 || loading}
								className={styles.submitButton}
							>
								{loading ? (
									<div className="flex items-center">
										<div className={styles.spinner} />
										Saving...
									</div>
								) : (
									"Continue"
								)}
							</Button>
						</div>
					</form>
				</CardContent>
			</Card>
		</motion.div>
	);
};
